import { useEffect, useState, type JSX } from 'react';

import { shaktiApi, ApiError } from '../api/client';
import { useProjectScopedEffect } from '../stores/project';
import { useProjectStore } from '../stores/project';

/**
 * K1.4 (round-5) — deploy panel.
 *
 * Round-3 F1.1 ships the Helm-chart generator at `POST /v1/deploy/generate`
 * and `GET /v1/deploy/artifacts`. Round-5 surfaces them: the PM/PO can
 * regenerate artifacts, see the list, and copy the `helm install`
 * one-liner to a terminal. In-dashboard `helm install` via trusted
 * sidecar is M3.2 (v2 polish); for now we copy the command.
 */
export function DeployRoute(): JSX.Element {
  const projectId = useProjectStore((s) => s.activeProjectId);
  const projectName = useProjectStore((s) =>
    s.projects.find((p) => p.id === s.activeProjectId)?.name,
  );
  const projectSlug = useProjectStore((s) =>
    s.projects.find((p) => p.id === s.activeProjectId)?.slug,
  );

  const [artifacts, setArtifacts] = useState<
    Array<{
      id: string;
      kind: string;
      path: string;
      size_bytes: number;
      created_at: string;
    }>
  >([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  async function refresh(): Promise<void> {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const list = await shaktiApi.listDeploymentArtifacts(projectId);
      setArtifacts(
        list.map((a) => ({
          id: a.id,
          kind: a.kind,
          path: a.path,
          size_bytes: a.size_bytes,
          created_at: a.created_at,
        })),
      );
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setLoading(false);
    }
  }

  // N5.5 / W1.1 (v1.1) — project-scoped; project switch refetches.
  useProjectScopedEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function regenerate(): Promise<void> {
    if (!projectId) return;
    setGenerating(true);
    setError(null);
    try {
      await shaktiApi.generateDeployment({
        project_id: projectId,
        app_name: projectSlug ?? 'shakti-app',
        target: 'helm',
      });
      await refresh();
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setGenerating(false);
    }
  }

  async function copyCommand(command: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(command);
      setCopied(command);
      setTimeout(() => setCopied(null), 2500);
    } catch {
      /* silent; fallback is the visible command text */
    }
  }

  if (!projectId) {
    return (
      <section className="mx-auto max-w-5xl">
        <h1 className="text-2xl font-semibold">Deploy</h1>
        <p className="mt-2 text-sm text-fg-secondary">
          Select a project from the nav sidebar to see its deployment artifacts.
        </p>
      </section>
    );
  }

  const appName = projectSlug ?? 'shakti-app';
  const helmInstall = `helm install ${appName} ./chart -f chart/values.yaml`;
  const helmUpgrade = `helm upgrade ${appName} ./chart -f chart/values.yaml`;
  const dockerBuild = `docker build -t ${appName}:latest .`;

  return (
    <section aria-labelledby="deploy-title" className="mx-auto flex max-w-5xl flex-col gap-6">
      <header>
        <span className="shakti-mono text-2xs uppercase tracking-wider text-fg-muted">
          /dashboard/deploy
        </span>
        <h1 id="deploy-title" className="text-2xl font-semibold text-fg-primary">
          Deploy
        </h1>
        <p className="text-sm text-fg-secondary">
          Shakti generates a full Helm chart + Dockerfile for {projectName ?? 'this project'}.
          Copy the one-liner below and run it against your cluster. In-dashboard deploy lands in
          v2 via a trusted sidecar.
        </p>
      </header>

      {error ? (
        <div
          role="alert"
          className="rounded-sm border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-200"
        >
          {error}
        </div>
      ) : null}

      <section className="shakti-card flex flex-col gap-4 p-6">
        <h2 className="text-lg font-semibold">Run it</h2>
        {/*
          #117 (round-6 close-out) — direct install via the
          shakti-helm-sidecar. Button stays disabled + tooltips with
          the operator-level env var so support can diagnose quickly
          when the sidecar isn't wired yet.
        */}
        <InDashboardInstall
          appName={appName}
          upgrade={false}
          label="First-time install (via Shakti sidecar)"
        />
        <InDashboardInstall
          appName={appName}
          upgrade={true}
          label="Upgrade an existing install (via Shakti sidecar)"
        />
        {/* T4.2 + T4.3 (v2.0) — rollout progress + one-click rollback. */}
        <ReleaseStatus release={appName} />
        <RollbackButton release={appName} />
        <CommandRow
          label="First-time install (copy command)"
          command={helmInstall}
          copied={copied === helmInstall}
          onCopy={() => copyCommand(helmInstall)}
        />
        <CommandRow
          label="Upgrade an existing install (copy command)"
          command={helmUpgrade}
          copied={copied === helmUpgrade}
          onCopy={() => copyCommand(helmUpgrade)}
        />
        <CommandRow
          label="Build the container image"
          command={dockerBuild}
          copied={copied === dockerBuild}
          onCopy={() => copyCommand(dockerBuild)}
        />
      </section>

      <section className="shakti-card flex flex-col gap-3 p-6" data-testid="deploy-artifacts">
        <header className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Generated artifacts</h2>
          <button
            type="button"
            onClick={regenerate}
            disabled={generating}
            className="rounded-sm bg-fg-primary px-3 py-1.5 text-xs font-semibold text-bg-base disabled:opacity-50"
            data-testid="regenerate-deployment"
          >
            {generating ? 'Generating…' : 'Regenerate'}
          </button>
        </header>
        {loading ? (
          <p className="text-xs text-fg-muted">Loading…</p>
        ) : artifacts.length === 0 ? (
          <p className="text-sm text-fg-secondary">
            No artifacts generated yet. Click Regenerate to produce the Helm chart, Dockerfile,
            and values.yaml for this project.
          </p>
        ) : (
          <table className="w-full text-xs" data-testid="artifacts-table">
            <thead>
              <tr className="text-left text-2xs uppercase tracking-wider text-fg-muted">
                <th className="py-2">Kind</th>
                <th>Path</th>
                <th>Size</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {artifacts.map((a) => (
                <tr key={a.id} className="border-t border-border-subtle">
                  <td className="py-2 font-semibold">{a.kind}</td>
                  <td className="shakti-mono">{a.path}</td>
                  <td className="text-2xs text-fg-muted">{formatBytes(a.size_bytes)}</td>
                  <td className="text-2xs text-fg-muted">{formatDate(a.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </section>
  );
}

function CommandRow({
  label,
  command,
  copied,
  onCopy,
}: {
  label: string;
  command: string;
  copied: boolean;
  onCopy: () => void;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-2xs uppercase tracking-wider text-fg-muted">{label}</span>
      <div className="flex items-center gap-2">
        <code className="shakti-mono flex-1 rounded-sm border border-border-subtle bg-bg-surface px-3 py-2 text-xs">
          {command}
        </code>
        <button
          type="button"
          onClick={onCopy}
          className="rounded-sm border border-border-subtle bg-bg-surface px-3 py-2 text-xs"
          aria-label={`Copy ${label} command`}
        >
          {copied ? 'Copied ✓' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}
function friendlyError(e: unknown): string {
  if (e instanceof ApiError) {
    return `${e.body?.error ?? e.message} (${e.status})`;
  }
  if (e instanceof Error) return e.message;
  return 'Unexpected error';
}

// #117 (round-6 close-out) — in-dashboard helm install via the sidecar.
// T4.2 (v2.0) — live rollout-status panel. Polls /v1/deploy/status
// every 5 s while the release is `pending-*` so reviewers watch
// the progress bar fill in real time.
function ReleaseStatus({ release }: { release: string }): JSX.Element {
  const [status, setStatus] = useState<{
    phase: string;
    revision: number | null;
    last_deployed: string | null;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const ac = new AbortController();
    shaktiApi
      .helmStatus(release, { signal: ac.signal })
      .then((r) =>
        setStatus({
          phase: r.phase,
          revision: r.revision,
          last_deployed: r.last_deployed,
        }),
      )
      .catch((e: unknown) => {
        if (ac.signal.aborted) return;
        setError(e instanceof Error ? e.message : 'status failed');
      });
    const timer = setInterval(
      () => setTick((n) => n + 1),
      status?.phase?.startsWith('pending') ? 5_000 : 30_000,
    );
    return () => {
      ac.abort();
      clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [release, tick]);

  if (error) {
    return (
      <div className="shakti-card text-xs text-fg-muted">
        Rollout status unavailable — sidecar not installed or release not found.
      </div>
    );
  }
  if (!status) {
    return <div className="shakti-card text-xs text-fg-muted">Loading status…</div>;
  }
  const tone =
    status.phase === 'deployed'
      ? 'bg-status-success/15 text-status-success border-status-success/30'
      : status.phase.startsWith('pending')
        ? 'bg-status-info/15 text-status-info border-status-info/30'
        : status.phase.startsWith('fail')
          ? 'bg-status-error/15 text-status-error border-status-error/30'
          : 'bg-bg-raised text-fg-secondary border-border-subtle';
  return (
    <div className={`shakti-card flex items-center justify-between border text-xs ${tone}`}>
      <div className="flex items-center gap-3">
        <span className="shakti-mono uppercase">{status.phase}</span>
        {status.revision !== null && (
          <span className="text-fg-muted">rev {status.revision}</span>
        )}
      </div>
      <span className="text-2xs text-fg-muted">
        {status.last_deployed ? new Date(status.last_deployed).toLocaleString() : ''}
      </span>
    </div>
  );
}

// T4.3 (v2.0) — one-click rollback button. Confirms before calling.
function RollbackButton({ release }: { release: string }): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  async function run(): Promise<void> {
    if (!window.confirm(`Roll back release "${release}" by one revision?`)) return;
    setBusy(true);
    setResult(null);
    try {
      const r = await shaktiApi.helmRollback(release, {});
      setResult(
        r.success
          ? `✓ rolled back ${r.previous_revision ?? '?'} → ${r.rolled_back_to ?? '?'}`
          : `✗ rollback failed: ${r.stderr}`,
      );
    } catch (e) {
      setResult(`✗ ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={() => void run()}
        disabled={busy}
        className="rounded-sm border border-status-warning/40 bg-status-warning/10 px-3 py-1.5 text-xs font-semibold text-status-warning disabled:opacity-50"
        title="Requires Admin capability + helm-sidecar. WebAuthn gate lands in Theme 2 phase D."
      >
        {busy ? 'Rolling back…' : 'Rollback one revision'}
      </button>
      {result && (
        <span className="shakti-mono text-2xs text-fg-muted">{result}</span>
      )}
    </div>
  );
}

function InDashboardInstall({
  appName,
  upgrade,
  label,
}: {
  appName: string;
  upgrade: boolean;
  label: string;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{
    ok: boolean;
    stdout: string;
    stderr: string;
  } | null>(null);

  async function run(): Promise<void> {
    setBusy(true);
    setResult(null);
    try {
      const resp = await shaktiApi.helmInstall({
        release: appName,
        chart: './chart',
        namespace: 'default',
        values_file: 'chart/values.yaml',
        set_values: [],
        upgrade,
      });
      setResult({ ok: resp.success, stdout: resp.stdout, stderr: resp.stderr });
    } catch (e) {
      setResult({
        ok: false,
        stdout: '',
        stderr: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        onClick={() => void run()}
        disabled={busy}
        className="self-start rounded-sm bg-accent px-3 py-1.5 text-xs font-semibold text-bg-base disabled:opacity-50"
        title="Requires shakti-helm-sidecar running on 127.0.0.1:9742 with SHAKTI_HELM_SIDECAR_TOKEN."
      >
        {busy ? 'Running…' : label}
      </button>
      {result && (
        <div
          role="status"
          className={`shakti-card text-xs ${
            result.ok ? 'border border-status-success/40' : 'border border-status-error/40 text-status-error'
          }`}
        >
          <div className="font-semibold">{result.ok ? '✓ helm completed' : '✗ helm failed'}</div>
          {result.stdout && (
            <pre className="mt-2 max-h-48 overflow-auto shakti-mono">{result.stdout}</pre>
          )}
          {result.stderr && (
            <pre className="mt-2 max-h-48 overflow-auto shakti-mono text-status-error">
              {result.stderr}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}
